import * as React from "react";
import { useNavigate, useSearchParams } from "react-router";
import { Mail } from "lucide-react";
import { format } from "date-fns";

import { AuthShell } from "@/components/auth/AuthShell";
import {
  AuthBanner,
  AuthField,
  AuthFoot,
  AuthHeading,
  AuthInput,
  AuthLink,
  AuthPrimaryButton,
  CodeInput,
  PasswordInput,
  PasswordRules,
  passwordMeetsPolicy,
} from "@/components/auth/AuthForm";
import { useConfirmForgotPassword, useForgotPassword } from "@/api/auth";
import { apiRequestId } from "@/api/error";
import { normalizeEmail } from "@/lib/email";

/**
 * /forgot-password — canvas 01b `auth/Forgot`, `ForgotCode`, `ForgotFailed`
 * (MV2-908). Two-phase Cognito OTP flow (backend.md §24.5).
 *
 * Request: email → POST /auth/forgot-password. The backend answers 200 for
 * unknown addresses (enumeration defence), so the only way to fail is a
 * real failure — and that is shown as one. The old page advanced to
 * "code sent" on any error.
 *
 * Code: the strip says when the code was sent and when it stops working
 * (Cognito's one-hour window), with Resend and Change email beside it.
 * One checklist, the rules once. Success → /login?reset=1.
 */

const HERO = {
  headline: "Back in two steps.",
  sub: "A one-time code by email, then a new password of your choosing.",
};

/** Cognito reset codes are valid for one hour. */
const CODE_TTL_MS = 60 * 60 * 1000;

type Phase = "request" | "code";

export function ForgotPasswordPage(): React.ReactElement {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  // MV2-120: ALWAYS start at the email step even with `?email=` — the
  // code is only sent when the user presses Send.
  const [phase, setPhase] = React.useState<Phase>("request");
  const [email, setEmail] = React.useState(params.get("email") ?? "");
  const [sentAt, setSentAt] = React.useState<Date | null>(null);
  const [sendFailure, setSendFailure] = React.useState<{
    requestId: string | null;
  } | null>(null);
  const [code, setCode] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [confirmError, setConfirmError] = React.useState<string | null>(null);

  const forgot = useForgotPassword();
  const confirmReset = useConfirmForgotPassword();

  const normalised = normalizeEmail(email);
  const confirmMismatch = confirm.length > 0 && confirm !== password;
  const canReset =
    code.length === 6 &&
    passwordMeetsPolicy(password) &&
    password === confirm &&
    !confirmReset.isPending;

  async function sendCode(): Promise<boolean> {
    setSendFailure(null);
    try {
      // MV2-350: case-sensitive Cognito pool — lowercase before the call.
      await forgot.mutateAsync({ email: normalised });
      setSentAt(new Date());
      return true;
    } catch (err) {
      setSendFailure({ requestId: apiRequestId(err) });
      return false;
    }
  }

  async function handleRequest(e: React.FormEvent) {
    e.preventDefault();
    if (!normalised) return;
    if (await sendCode()) setPhase("code");
  }

  async function handleResend() {
    setConfirmError(null);
    setCode("");
    await sendCode();
  }

  function changeEmail() {
    setPhase("request");
    setSendFailure(null);
    setConfirmError(null);
    setCode("");
  }

  async function handleConfirm(e: React.FormEvent) {
    e.preventDefault();
    if (!canReset) return;
    setConfirmError(null);
    try {
      await confirmReset.mutateAsync({
        email: normalised,
        code,
        new_password: password,
      });
      navigate(`/login?email=${encodeURIComponent(normalised)}&reset=1`, {
        replace: true,
      });
    } catch (err) {
      setConfirmError(resetSentence(err));
    }
  }

  const backToSignIn = (
    <AuthLink
      to={`/login${normalised ? `?email=${encodeURIComponent(normalised)}` : ""}`}
    >
      ← Back to sign in
    </AuthLink>
  );

  if (phase === "code" && sentAt) {
    const validUntil = new Date(sentAt.getTime() + CODE_TTL_MS);
    return (
      <AuthShell {...HERO}>
        <form onSubmit={handleConfirm} noValidate>
          <AuthHeading
            title="Check your email"
            sub={
              <>
                We sent a code to{" "}
                <b className="font-semibold text-gray-900">{normalised}</b>.
                Enter it with your new password.
              </>
            }
          />

          {/* Board: the sent / valid-until strip on --surface-sunk with Resend · Change email. */}
          <div className="mb-3.5 flex items-center gap-2 rounded-[8px] bg-surface-sunk px-2.5 py-2 text-[12.5px] text-gray-900">
            <Mail size={15} aria-hidden className="flex-none text-gray-500" />
            <span>
              Code sent{" "}
              <span className="tabular">{format(sentAt, "HH:mm")}</span> · valid
              until{" "}
              <b className="tabular font-semibold">
                {format(validUntil, "HH:mm")}
              </b>
            </span>
            <span className="flex-1" />
            <AuthLink onClick={() => void handleResend()}>
              {forgot.isPending ? "Sending…" : "Resend"}
            </AuthLink>
            <span className="text-[11px] text-gray-500">·</span>
            <AuthLink onClick={changeEmail}>Change email</AuthLink>
          </div>

          {sendFailure && (
            <AuthBanner tone="bad" align="start" className="mb-3.5">
              <SendFailedCopy requestId={sendFailure.requestId} />
            </AuthBanner>
          )}
          {confirmError && (
            <AuthBanner tone="bad" className="mb-3.5">
              {confirmError}
            </AuthBanner>
          )}

          <div className="flex flex-col gap-3.5">
            <AuthField label="Reset code" htmlFor="reset-code">
              <CodeInput
                id="reset-code"
                value={code}
                onChange={setCode}
                size="md"
                autoFocus
                aria-label="Reset code"
                disabled={confirmReset.isPending}
              />
            </AuthField>
            <AuthField label="New password" htmlFor="reset-password">
              <PasswordInput
                id="reset-password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={confirmReset.isPending}
              />
            </AuthField>
            <PasswordRules password={password} />
            <AuthField
              label="Confirm new password"
              htmlFor="reset-confirm"
              err={confirmMismatch ? "Passwords don't match yet." : undefined}
            >
              <PasswordInput
                id="reset-confirm"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                invalid={confirmMismatch}
                disabled={confirmReset.isPending}
              />
            </AuthField>
          </div>

          <div className="mt-5">
            <AuthPrimaryButton
              type="submit"
              loading={confirmReset.isPending}
              loadingLabel="Resetting…"
              disabled={!canReset}
            >
              Reset password
            </AuthPrimaryButton>
          </div>
          <AuthFoot left={backToSignIn} />
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell {...HERO}>
      <form onSubmit={handleRequest} noValidate>
        <AuthHeading
          title="Reset your password"
          sub="Enter your email — we'll send a one-time code."
        />

        {sendFailure && (
          <AuthBanner tone="bad" align="start" className="mb-3.5">
            <SendFailedCopy requestId={sendFailure.requestId} />
          </AuthBanner>
        )}

        <AuthField label="Email" htmlFor="forgot-email">
          <AuthInput
            id="forgot-email"
            type="email"
            required
            autoFocus
            autoComplete="email"
            icon={<Mail size={15} />}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={forgot.isPending}
          />
        </AuthField>

        <div className="mt-5">
          <AuthPrimaryButton
            type="submit"
            loading={forgot.isPending}
            loadingLabel="Sending code…"
            disabled={!normalised}
          >
            {sendFailure ? "Try again" : "Send reset code"}
          </AuthPrimaryButton>
        </div>
        <p className="mt-3 text-[11px] leading-[1.4] text-gray-500">
          If the email matches an account, a code arrives within a minute and is
          valid for 1 hour.
        </p>
        <AuthFoot left={backToSignIn} />
      </form>
    </AuthShell>
  );
}

/** Board `auth/ForgotFailed`: what happened, the request id, and that nothing leaked. */
function SendFailedCopy({
  requestId,
}: {
  requestId: string | null;
}): React.ReactElement {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[12.5px] font-medium">
        We couldn&apos;t send the code.
      </span>
      <span className="text-[11px] leading-[1.4] text-gray-500">
        The sign-in service didn&apos;t respond
        {requestId ? (
          <>
            {" "}
            (request <span className="font-mono tabular">{requestId}</span>)
          </>
        ) : null}
        . Nothing about your account is revealed by this — try again in a
        moment.
      </span>
    </div>
  );
}

/** One sentence per Cognito reason (the backend forwards the code in its 401/422 message). */
function resetSentence(err: unknown): string {
  const message = err instanceof Error ? err.message : "";
  if (/CodeMismatch|ExpiredCode|invalid code/i.test(message)) {
    return "That code didn't match or has expired. Check the email, or resend a code.";
  }
  if (/InvalidPassword/i.test(message)) {
    return "Cognito rejected that password — it must meet every rule above.";
  }
  if (/LimitExceeded|rate limit/i.test(message)) {
    return "Too many attempts for now. Wait a few minutes, then try again.";
  }
  return message || "We couldn't reset the password. Try again in a moment.";
}
